import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useApp } from "@/contexts/AppContext";
import { useToast } from "@/hooks/use-toast";
import { CHAT_THEMES, DEFAULT_THEME_ID, getTheme, ChatTheme } from "@/lib/chatThemes";

// Midnight is the implicit default for every account — always considered owned
// so applying it works even before the user has any purchases recorded.
const IMPLICIT_OWNED: string[] = [DEFAULT_THEME_ID];

interface ChatThemeContextType {
  activeThemeId: string;
  activeTheme: ChatTheme;
  ownedThemeIds: string[];
  loading: boolean;
  purchasing: string | null;
  applying: string | null;
  isOwned: (id: string) => boolean;
  purchaseTheme: (id: string) => Promise<{ success: boolean; error?: string }>;
  applyTheme: (id: string) => Promise<{ success: boolean; error?: string }>;
  refresh: () => Promise<void>;
}

const ChatThemeContext = createContext<ChatThemeContextType | undefined>(undefined);

export const ChatThemeProvider = ({ children }: { children: ReactNode }) => {
  const { user, refreshBalance } = useApp();
  const { toast } = useToast();
  const [activeThemeId, setActiveThemeId] = useState<string>(DEFAULT_THEME_ID);
  const [ownedThemeIds, setOwnedThemeIds] = useState<string[]>(IMPLICIT_OWNED);
  const [loading, setLoading] = useState(false);
  const [purchasing, setPurchasing] = useState<string | null>(null);
  const [applying, setApplying] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const [{ data: profile }, { data: purchases }] = await Promise.all([
        supabase.from("profiles").select("active_chat_theme").eq("user_id", user.id).maybeSingle(),
        supabase.from("user_purchases").select("item_id").eq("user_id", user.id).eq("item_type", "chat_theme"),
      ]);
      const owned = new Set<string>(IMPLICIT_OWNED);
      (purchases ?? []).forEach((p: any) => owned.add(p.item_id));
      setOwnedThemeIds(Array.from(owned));
      setActiveThemeId((profile as any)?.active_chat_theme ?? DEFAULT_THEME_ID);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) void refresh();
    else {
      setActiveThemeId(DEFAULT_THEME_ID);
      setOwnedThemeIds(IMPLICIT_OWNED);
    }
  }, [user?.id, refresh]);

  const isOwned = useCallback((id: string) => ownedThemeIds.includes(id), [ownedThemeIds]);

  const purchaseTheme = useCallback(
    async (id: string) => {
      const theme = CHAT_THEMES.find((t) => t.id === id);
      if (!theme) return { success: false, error: "Theme not found" };
      if (isOwned(id)) return { success: true };
      setPurchasing(id);
      try {
        const { data, error } = await supabase.rpc("purchase_chat_theme", {
          p_theme_id: id,
          p_price: theme.price,
        });
        if (error) throw error;
        const result = data as any;
        if (!result?.success) {
          const err = result?.error ?? "purchase_failed";
          if (err === "insufficient_coins") {
            toast({ title: "Not enough coins", description: `You need ${theme.price} coins.`, variant: "destructive" });
          } else {
            toast({ title: "Purchase failed", description: err, variant: "destructive" });
          }
          return { success: false, error: err };
        }
        setOwnedThemeIds((prev) => Array.from(new Set([...prev, id])));
        await refreshBalance();
        toast({ title: "Theme unlocked! 🎨", description: theme.name });
        return { success: true };
      } catch (e: any) {
        toast({ title: "Purchase failed", description: e.message, variant: "destructive" });
        return { success: false, error: e.message };
      } finally {
        setPurchasing(null);
      }
    },
    [isOwned, refreshBalance, toast]
  );

  const applyTheme = useCallback(
    async (id: string) => {
      if (!isOwned(id)) return { success: false, error: "not_owned" };
      setApplying(id);
      const prev = activeThemeId;
      setActiveThemeId(id); // optimistic
      try {
        const { data, error } = await supabase.rpc("set_active_chat_theme", { p_theme_id: id });
        if (error) throw error;
        const result = data as any;
        if (!result?.success) {
          setActiveThemeId(prev);
          return { success: false, error: result?.error ?? "apply_failed" };
        }
        return { success: true };
      } catch (e: any) {
        setActiveThemeId(prev);
        toast({ title: "Failed to apply theme", description: e.message, variant: "destructive" });
        return { success: false, error: e.message };
      } finally {
        setApplying(null);
      }
    },
    [activeThemeId, isOwned, toast]
  );

  return (
    <ChatThemeContext.Provider
      value={{
        activeThemeId,
        activeTheme: getTheme(activeThemeId),
        ownedThemeIds,
        loading,
        purchasing,
        applying,
        isOwned,
        purchaseTheme,
        applyTheme,
        refresh,
      }}
    >
      {children}
    </ChatThemeContext.Provider>
  );
};

export const useChatTheme = () => {
  const ctx = useContext(ChatThemeContext);
  if (!ctx) throw new Error("useChatTheme must be used within ChatThemeProvider");
  return ctx;
};